import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileSignature, Plus, Search, Download, ArrowRight, Check, X, Trash2, Percent,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, fmtDate, titleCase } from "@/lib/format";
import { useCanEdit } from "@/context/AuthContext";
import { CardList, DataCard, TableWrap } from "@/components/DataCards";
import Pagination, { usePagedList, downloadFile } from "@/components/Pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_TONE = {
  DRAFT: "bg-slate-100 text-slate-600",
  SENT: "bg-blue-50 text-blue-700",
  ACCEPTED: "bg-emerald-50 text-emerald-700",
  DECLINED: "bg-rose-50 text-rose-700",
  EXPIRED: "bg-amber-50 text-amber-700",
  CONVERTED: "bg-violet-50 text-violet-700",
};

const Chip = ({ status }) => (
  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${
    STATUS_TONE[status] || "bg-slate-100 text-slate-600"}`}>
    {titleCase(status || "")}
  </span>
);

const Tile = ({ icon: Icon, label, value, sub, tone = "text-blue-500", testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-slate-500">
      <Icon size={13} className={tone} /> {label}
    </div>
    <div className="mt-2 font-heading text-2xl font-bold tabular-nums">{value}</div>
    {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
  </div>
);

export default function Quotations() {
  const canEdit = useCanEdit();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("ALL");
  const [status, setStatus] = useState("ALL");

  const query = useMemo(() => ({
    q: q || undefined,
    kind: kind === "ALL" ? undefined : kind,
    status: status === "ALL" ? undefined : status,
  }), [q, kind, status]);

  const { rows, total, page, setPage, size, setSize, loading, reload } =
    usePagedList("/quotations", query);

  const loadSummary = useCallback(() => {
    api.get("/quotations/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const refresh = () => { reload(); loadSummary(); };

  const setStatusOf = async (row, next) => {
    try {
      await api.post(`/quotations/${row.id}/status`, { status: next });
      toast.success(`Marked ${next.toLowerCase()}`);
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const convert = async (row) => {
    try {
      const { data } = await api.post(`/quotations/${row.id}/convert`);
      toast.success("Draft invoice created from this quotation");
      navigate(`/invoices/${data.document_id}/edit`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.number || "this draft"}?`)) return;
    try {
      await api.delete(`/quotations/${row.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const actionsFor = (row) => (
    <>
      <button className="text-xs text-blue-700 hover:text-blue-900 transition-colors duration-150"
        onClick={() => downloadFile(`/quotations/${row.id}/pdf`, {}, "quotation.pdf")}
        data-testid={`quote-pdf-${row.id}`}>PDF</button>
      {canEdit && row.status !== "CONVERTED" && (
        <button className="text-xs text-slate-500 hover:text-slate-800 transition-colors duration-150"
          onClick={() => navigate(`/quotations/${row.id}/edit`)}
          data-testid={`quote-edit-${row.id}`}>Edit</button>
      )}
      {canEdit && ["SENT", "ACCEPTED"].includes(row.status) && (
        <button className="text-xs font-medium text-violet-700 hover:text-violet-900 transition-colors duration-150"
          onClick={() => convert(row)} data-testid={`quote-convert-${row.id}`}>
          Convert to invoice
        </button>
      )}
      {canEdit && row.status === "SENT" && (
        <>
          <button className="text-xs text-emerald-700 hover:text-emerald-900 transition-colors duration-150"
            onClick={() => setStatusOf(row, "ACCEPTED")}
            data-testid={`quote-accept-${row.id}`}>Accepted</button>
          <button className="text-xs text-slate-400 hover:text-rose-600 transition-colors duration-150"
            onClick={() => setStatusOf(row, "DECLINED")}
            data-testid={`quote-decline-${row.id}`}>Declined</button>
        </>
      )}
      {canEdit && row.status !== "CONVERTED" && (
        <button className="text-xs text-slate-400 hover:text-rose-600 transition-colors duration-150"
          onClick={() => remove(row)} data-testid={`quote-delete-${row.id}`}>Delete</button>
      )}
    </>
  );

  return (
    <div className="space-y-5" data-testid="quotations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
            Quotations
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            What you offered, before it became an invoice. Nothing here touches your
            GST returns until you convert it.
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button data-testid="new-quotation-btn" onClick={() => navigate("/quotations/new")}
              className="gap-1.5 rounded-full bg-blue-600 hover:bg-blue-700">
              <Plus size={16} /> New Quotation
            </Button>
            <Button variant="outline" data-testid="new-proforma-btn"
              onClick={() => navigate("/proforma/new")} className="gap-1.5 rounded-full">
              <Plus size={16} /> Proforma
            </Button>
          </div>
        )}
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Tile icon={FileSignature} label="Open" value={summary.open.count}
            sub={inr(summary.open.amount)} testid="quote-tile-open" />
          <Tile icon={Check} label="Accepted" value={summary.accepted.count}
            sub={inr(summary.accepted.amount)} tone="text-emerald-500"
            testid="quote-tile-accepted" />
          <Tile icon={X} label="Expired" value={summary.expired} tone="text-amber-500"
            testid="quote-tile-expired" />
          <Tile icon={Percent} label="Win rate"
            value={summary.win_rate === null ? "—" : `${summary.win_rate}%`}
            sub="of the ones answered" tone="text-slate-400" testid="quote-tile-winrate" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[180px] flex-1 sm:max-w-xs">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="h-9 pl-8" placeholder="Number, customer or reference"
            data-testid="quotes-search" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-9 w-full sm:w-40" data-testid="quotes-kind-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All types</SelectItem>
            <SelectItem value="QUOTATION">Quotations</SelectItem>
            <SelectItem value="PROFORMA">Proforma</SelectItem>
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-full sm:w-44" data-testid="quotes-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Any status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="SENT">Sent</SelectItem>
            <SelectItem value="ACCEPTED">Accepted</SelectItem>
            <SelectItem value="DECLINED">Declined</SelectItem>
            <SelectItem value="EXPIRED">Expired</SelectItem>
            <SelectItem value="CONVERTED">Converted</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <TableWrap>
        <table className="w-full text-sm" data-testid="quotations-table">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5 text-left font-semibold">Number</th>
              <th className="px-3 py-2.5 text-left font-semibold">Date</th>
              <th className="px-3 py-2.5 text-left font-semibold">Customer</th>
              <th className="px-3 py-2.5 text-left font-semibold">Valid until</th>
              <th className="px-3 py-2.5 text-right font-semibold">Amount</th>
              <th className="px-3 py-2.5 text-left font-semibold">Status</th>
              <th className="px-4 py-2.5 text-right font-semibold"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50 transition-colors duration-150"
                data-testid={`quote-row-${r.id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-mono text-xs font-semibold">{r.number || "Draft"}</div>
                  <div className="text-[10px] text-slate-400">{r.kind_label}</div>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{fmtDate(r.quote_date)}</td>
                <td className="px-3 py-2.5">{r.party_name}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">
                  {fmtDate(r.valid_until)}
                </td>
                <td className="px-3 py-2.5 text-right font-mono tabular-nums font-semibold">
                  {inr(r.totals?.grand_total)}
                </td>
                <td className="px-3 py-2.5"><Chip status={r.display_status} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex flex-wrap items-center justify-end gap-3">{actionsFor(r)}</div>
                </td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={7} className="px-4 py-16 text-center">
                <FileSignature size={26} className="mx-auto text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-700">No quotations yet</p>
                <p className="mt-1 text-xs text-slate-500">
                  Quote a price, send it, and turn it into an invoice when they say yes.
                </p>
              </td></tr>
            )}
          </tbody>
        </table>
      </TableWrap>

      <CardList empty="No quotations yet">
        {rows.map((r) => (
          <DataCard key={r.id} testid={`quote-card-${r.id}`}
            title={r.number || "Draft"} subtitle={`${r.party_name} · ${fmtDate(r.quote_date)}`}
            right={<>
              <div className="font-mono text-sm font-bold tabular-nums">{inr(r.totals?.grand_total)}</div>
              <div className="mt-1"><Chip status={r.display_status} /></div>
            </>}
            rows={[["Type", r.kind_label], ["Valid until", fmtDate(r.valid_until)]]}
            actions={actionsFor(r)} />
        ))}
      </CardList>

      <Pagination total={total} page={page} setPage={setPage} size={size} setSize={setSize}
        loading={loading} testid="quotes-pagination" />
    </div>
  );
}
